/**
 * Admin V3 — Subscriptions — DORMANT-STATE PLACEHOLDER
 *
 * Mounted at: /admin-v3/subscriptions (see AdminV3.js)
 *
 * GOVERNANCE — DORMANT FEATURE:
 *   Driver subscriptions are not part of the current operational model.
 *   Per `docs/BUSINESS_MISSION_ru.md:1285`, subscriptions are explicitly NOT
 *   used in Phase 1. Per owner ruling 2026-05-26, the route is kept reachable
 *   in V3 but renders a clear disabled-state page instead of a misleading
 *   data table.
 *
 *   See: docs/SUBSCRIPTIONS_DORMANT_2026-05-26.md for the full governance
 *   record and backlog (S1-S7) required to re-activate this feature.
 *
 * IMPLEMENTATION GUARANTEES:
 *   - ZERO HTTP calls (no useAdminQuery, no useAdminMutation, no axios, no
 *     fetch). Pure presentation surface.
 *   - ZERO references to /admin/subscriptions or
 *     /admin/drivers/{id}/subscription endpoints.
 *   - ZERO references to backend collections.
 *   - ZERO interactive controls (no buttons, no toggles, no form inputs).
 *   - The backend subscription code paths remain deployed and reachable from
 *     the legacy admin; they are dormant in V3 only.
 *
 * COPY (per owner specification):
 *   - "Fonctionnalité désactivée pour le moment" (heading)
 *   - "Les abonnements chauffeurs ne sont pas utilisés dans le modèle
 *     opérationnel actuel." (description)
 *   - "Aucune action n'est disponible." (no-action confirmation)
 */

import React from 'react';
import { CreditCard, PowerOff } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';
import { t, DEFAULT_LOCALE } from '../../i18n';

const SubscriptionsDisabledPage = () => {
  const locale = DEFAULT_LOCALE;

  return (
    <SectionCard
      title={t('subscriptions.disabled.title', locale)}
      icon={CreditCard}
      iconClass="bg-slate-100 text-slate-500"
    >
      <div
        className="flex flex-col items-center justify-center py-14 text-center"
        data-testid="adminv3-subscriptions-disabled"
      >
        <div className={cn(
          'mb-5 flex h-16 w-16 items-center justify-center rounded-full',
          'bg-slate-100 text-slate-400'
        )}>
          <PowerOff className="h-7 w-7" />
        </div>

        <h2
          className="text-[18px] font-semibold text-slate-700 mb-2"
          data-testid="adminv3-subscriptions-disabled-heading"
        >
          {t('subscriptions.disabled.heading', locale)}
        </h2>

        <p
          className="text-[13px] text-slate-500 max-w-md leading-relaxed"
          data-testid="adminv3-subscriptions-disabled-description"
        >
          {t('subscriptions.disabled.description', locale)}
        </p>

        <p
          className="mt-3 text-[12px] italic text-slate-400"
          data-testid="adminv3-subscriptions-disabled-no-actions"
        >
          {t('subscriptions.disabled.noActions', locale)}
        </p>
      </div>
    </SectionCard>
  );
};

export default SubscriptionsDisabledPage;
